import type { ItemDetail } from "../../data/catalog.ts";
import { capitalize } from "../utils.ts";
import type { AttrRowSpec } from "./AttrRow.tsx";

type AmmoAttrs = NonNullable<ItemDetail["ammoAttributes"]>;

// Short bullet-type labels for display. Enum values are lowercase keys from
// the schema; we uppercase common acronyms (FMJ, JHP, SP, HP) and
// title-case the rest for the detail row.
function formatBulletType(t: AmmoAttrs["bulletType"]): string | null {
  if (!t) return null;
  switch (t) {
    case "fmj":
      return "FMJ";
    case "jhp":
      return "JHP";
    case "sp":
      return "SP";
    case "hp":
      return "HP";
    default:
      return capitalize(t);
  }
}

export function AmmoSpecLine({ a }: { a: AmmoAttrs }) {
  // One-line glance summary for the floor: caliber · grain · rounds/box.
  // Mirrors FirearmSpecLine's role.
  const parts: string[] = [a.caliberGauge];
  if (a.grain != null) parts.push(`${a.grain}gr`);
  if (a.roundsPerBox) parts.push(`${a.roundsPerBox}/box`);
  return <p className="text-[15px] font-medium text-slate-600 tabular-nums">{parts.join(" · ")}</p>;
}

export function buildAmmoRows(a: AmmoAttrs): AttrRowSpec[] {
  return [
    { label: "Manufacturer", value: a.manufacturer, required: true },
    { label: "Caliber / gauge", value: a.caliberGauge, mono: true, required: true },
    { label: "Grain", value: a.grain != null ? `${a.grain} gr` : null, mono: true },
    { label: "Bullet type", value: formatBulletType(a.bulletType) },
    { label: "Hollow point", value: a.isHollowPoint ? "Yes" : "No" },
    { label: "Rounds per box", value: `${a.roundsPerBox}`, mono: true, required: true },
    {
      label: "Muzzle velocity",
      value: a.muzzleVelocityFps != null ? `${a.muzzleVelocityFps} fps` : null,
      mono: true,
    },
  ];
}
